import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { CustomNotification } from 'common/components';

import * as productServices from 'services/product';
import * as walmartServices from 'services/walmart';

const queryWalmartKeys = {
  walmartProductSchema: (productIds) => [
    'walmart',
    'get-walmart-product-schema',
    ...(productIds?.length ? [productIds] : []),
  ],

  initialWalmartProductData: (productIds) => [
    'walmart',
    'get-walmart-product-data',
    ...(productIds?.length ? [productIds] : []),
  ],
  walmartProductAssets: ['walmart', 'get-walmart-product-assets'],
  validateProductData: ['walmart', 'validate-walmart-product-data'],
  saveWalmartWhenClose: ['walmart', 'save-walmart-data-setting'],
};

export const usePostToWalmart = () => {
  return useMutation({
    mutationFn: async (params) => {
      const response = await productServices.postToWalmart(params);

      const { isSuccess, message } = response;

      if (isSuccess) {
        return Promise.resolve();
      } else {
        return Promise.reject(message || 'Server Error');
      }
    },
  });
};

export const useGetWalmartProductSchema = ({ productIds, ...options }) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryWalmartKeys.walmartProductSchema(productIds),
    queryFn: async () => {
      const { isSuccess, data, message } =
        await walmartServices.getWalmartProductSchema({
          productIds,
        });

      if (isSuccess) {
        return data;
      } else {
        const error = message || 'Server Error';
        return Promise.reject(error);
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: false,
    ...options,
  });

  const removeCache = () => {
    queryClient.removeQueries({
      queryKey: queryWalmartKeys.walmartProductSchema(),
    });
  };

  const invalidateWalmartProductSchema = () => {
    queryClient.invalidateQueries({
      queryKey: queryWalmartKeys.walmartProductSchema(),
    });
  };

  return { ...query, removeCache, invalidateWalmartProductSchema };
};

export const useGetWalmartProductData = ({
  productIds,
  isGetLatestProductData,
  successCallback,
  ...options
}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryWalmartKeys.initialWalmartProductData(productIds),
    queryFn: async () => {
      const { isSuccess, data, message } =
        await walmartServices.getInitialWalmartProductData({
          productIds,
          isGetLatestProductData,
        });

      if (isSuccess) {
        const walmartProductDataList = data?.['productMatchingData'];

        successCallback && successCallback(walmartProductDataList);

        return walmartProductDataList;
      } else {
        const error = message || 'Server Error';
        return Promise.reject(error);
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: false,
    ...options,
  });

  const invalidateWalmartProductData = () => {
    queryClient.invalidateQueries({
      queryKey: queryWalmartKeys.initialWalmartProductData(),
    });
  };

  const removeCache = () => {
    queryClient.removeQueries({
      queryKey: queryWalmartKeys.initialWalmartProductData(),
    });
  };

  return { ...query, removeCache, invalidateWalmartProductData };
};

export const useMutateValidateWalmartFormData = () => {
  const mutation = useMutation({
    mutationKey: queryWalmartKeys.validateProductData,
    mutationFn: async (params) => {
      const response = await walmartServices.validateWalmartProductData(params);

      const { isSuccess, data, message } = response;

      if (isSuccess) {
        return Promise.resolve(data?.['dataValidations']);
      } else {
        return Promise.reject(message || 'Server Error');
      }
    },
    onError: (e) => {
      CustomNotification.error(e);
    },
  });

  const isMutating = useIsMutating({
    mutationKey: queryWalmartKeys.validateProductData,
  });

  return { ...mutation, isMutating };
};

export const useGetWalmartProductAssets = ({ productId, ...options }) => {
  return useQuery({
    queryKey: queryWalmartKeys.walmartProductAssets,
    queryFn: async () => {
      const response = await walmartServices.getWalmartProductAssets({
        productId,
      });

      const { isSuccess, data, message } = response || {};

      if (isSuccess) {
        return data?.['productAssets'];
      } else {
        return Promise.reject(message || 'Server Error');
      }
    },
    enabled: !!productId,
    refetchOnWindowFocus: true,
    staleTime: 0,
    ...options,
  });
};

export const useSaveWalmartFormValueWhenExit = () => {
  const saveWalmartFormMutation = useMutation({
    mutationKey: queryWalmartKeys.saveWalmartWhenClose,
    mutationFn: async (params) => {
      const response = await productServices.savePostToWalmart(params);

      const { isSuccess, message } = response || {};
      if (isSuccess) {
        return Promise.resolve(response || {});
      } else {
        return Promise.reject(message || 'Sever Error');
      }
    },
    onError: (e) => {
      CustomNotification.error(e);
    },
  });

  const isSaveFormWalmartMutating = useIsMutating({
    mutationKey: queryWalmartKeys.saveWalmartWhenClose,
  });

  return { saveWalmartFormMutation, isSaveFormWalmartMutating };
};
